import { api, qs } from "../api/client";
import type { Segment, SegmentKind, SegmentPage } from "../api/types";

/**
 * The segment before or after this one, of the same kind.
 *
 * Asked of the server rather than of whatever the timeline happens to have cached: the
 * neighbour of the block at the edge of the viewport is off screen by definition, and
 * "next" has to keep working there.
 *
 * The window filters are half-open on purpose. `start` selects segments whose end is after
 * it, so passing the current segment's end excludes the current segment itself; `end`
 * selects segments beginning before it, so passing the current start does the same going
 * backwards.
 */
export async function neighbourSegment(
  direction: -1 | 1,
  current: Segment,
): Promise<Segment | null> {
  const query =
    direction === 1
      ? qs({ start: current.end, kind: current.kind, order: "start_asc", limit: 1 })
      : qs({ end: current.start, kind: current.kind, order: "start_desc", limit: 1 });
  const page = await api.get<SegmentPage>(`/api/segments${query}`);
  return page.items[0] ?? null;
}

/** The segment covering an instant, used to label playback that began from the timeline. */
export async function segmentAt(ms: number, kind?: SegmentKind): Promise<Segment | null> {
  const at = new Date(ms).toISOString();
  const query = qs({
    start: at,
    end: new Date(ms + 1000).toISOString(),
    kind,
    order: "start_desc",
    limit: 1,
  });
  const page = await api.get<SegmentPage>(`/api/segments${query}`);
  return page.items[0] ?? null;
}

/** The most recent segment of a kind, for starting playback with nothing selected. */
export async function latestSegment(kind: SegmentKind): Promise<Segment | null> {
  const page = await api.get<SegmentPage>(
    `/api/segments${qs({ kind, order: "start_desc", limit: 1 })}`,
  );
  return page.items[0] ?? null;
}
